import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "@/components/common/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";

import { Button } from "components/common/Button";
import { Card } from "components/common/Card";
import { CopyButton } from "components/common/CopyButton";
import { Dialog } from "components/main/utils/Dialog";
import { TooltipButton } from "components/common/TooltipButton";
import { InlineError } from "components/common/InlineError";
import { SavedConnectionEditor } from "components/main/utils/settings/SavedConnectionEditor";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { findConnection, useConnectionsStore } from "stores/connectionsStore";
import { TaskType } from "Worker/entities";
import {
  checkOpfsCapable,
  reattachLocalSqlite,
  type ReattachFlowResult,
} from "components/canvas/leftSide/import/LocalSqlite";
import {
  deriveSqliteHandleState,
  setOpfsNameForHandle,
  type SqliteHandleState,
} from "stores/sqliteHandles";
import {
  attachSqliteToOpfsViaWorker,
  pickSqliteFile,
} from "components/main/utils/sqlite/sqliteAttachShared";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";
import { formatRelativeTime } from "./formatRelativeTime";
import { useGoogleOauthPopup } from "./useGoogleOauthPopup";
import { ConnectionAssetIcon } from "./ConnectionAssetIcon";
import {
  BUCKET_PROVIDER_LABEL,
  connectionTypeLabel,
} from "./ConnectionTypeIcon";
import {
  hasGoogleSheetsScope,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";

interface Props {
  open: boolean;
  connection: SavedConnection | null;
  onClose: () => void;
  onSaved: () => void;
}

export const ConnectionEditModal: React.FC<Props> = ({
  open,
  connection,
  onClose,
  onSaved,
}) => {
  if (!connection) return null;
  const ctype = connection.credentials.type;
  const isGoogle = ctype === "google_oauth";
  const isBucket = ctype === "bucket";
  const isLocalSqlite = ctype === "local_sqlite";
  const compact = isGoogle || isBucket || isLocalSqlite;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={<ConnectionHeader connection={connection} />}
      autoSpacing={false}
      className={compact ? "max-w-110 min-w-100" : "max-w-140 min-w-100"}
    >
      {isGoogle ? (
        <GoogleConnectionView
          connection={connection}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : isBucket ? (
        <BucketConnectionView
          connection={connection}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : isLocalSqlite ? (
        // Local SQLite has no editable server-side credentials, but the
        // overlay still surfaces per-device attach state, file
        // metadata, and reconnect / cache / delete actions. Matches the
        // Google overlay pattern.
        <LocalSqliteConnectionView
          connection={connection}
          onClose={onClose}
          onSaved={onSaved}
        />
      ) : (
        <SavedConnectionEditor
          existing={connection}
          onSaved={() => {
            onSaved();
            onClose();
          }}
          onCancel={onClose}
          hideCancel
        />
      )}
    </Dialog>
  );
};

const ConnectionHeader: React.FC<{ connection: SavedConnection }> = ({
  connection,
}) => {
  const cr = connection.credentials;
  const subtitle =
    cr.type === "google_oauth"
      ? cr.account_email
      : cr.type === "bucket"
        ? `${BUCKET_PROVIDER_LABEL[cr.provider]} · ${
            cr.scope.kind === "bucket" ? cr.scope.name : "entire account"
          }`
        : cr.type === "local_sqlite"
          ? "Local file"
          : `${cr.host}:${cr.port}/${cr.database}`;
  return (
    <div className="flex items-center gap-3 min-w-0">
      <ConnectionAssetIcon type={cr.type} size="md" />
      <div className="flex-1 min-w-0">
        <p className="text-fg-bold leading-tight">
          {connectionTypeLabel(cr.type)}
        </p>
        <p className="text-fg-muted-regular-small font-mono truncate">
          {subtitle}
        </p>
      </div>
    </div>
  );
};

const LocalSqliteConnectionView: React.FC<{
  connection: SavedConnection;
  onClose: () => void;
  onSaved: () => void;
}> = ({ connection, onClose, onSaved }) => {
  const cr =
    connection.credentials.type === "local_sqlite"
      ? connection.credentials
      : null;
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [pendingClearCache, setPendingClearCache] = useState(false);
  const [pendingReconnect, setPendingReconnect] = useState(false);
  const [sqliteState, setSqliteState] = useState<SqliteHandleState | null>(
    null,
  );
  const remove = useConnectionsStore((s) => s.remove);
  const refetch = useConnectionsStore((s) => s.refetch);
  const sendMessageToWorker = useBoundStore((s) => s.sendMessageToWorker);
  const worker = useBoundStore((s) => s.worker);
  const initializeWorker = useBoundStore((s) => s.initializeWorker);
  // Worker is normally booted by CanvasApp; boot it here when the
  // modal is opened from /connections without a canvas mounted, since
  // reconnect needs the OPFS attach task. Idempotent.
  useEffect(() => {
    if (!worker) initializeWorker();
  }, [worker, initializeWorker]);
  const capability = checkOpfsCapable();

  // Load the per-device handle state. Re-runs after a successful
  // reconnect / cache clear via `bumpState`.
  const [stateBump, setStateBump] = useState(0);
  const bumpState = useCallback(() => setStateBump((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const state = await deriveSqliteHandleState(connection.id);
      if (!cancelled) setSqliteState(state);
    })();
    return () => {
      cancelled = true;
    };
  }, [connection.id, stateBump]);

  if (!cr) return null;

  const isAttached = sqliteState?.kind === "attached";
  const needsReconnect =
    sqliteState !== null &&
    (sqliteState.kind === "needs-reattach-this-device" ||
      sqliteState.kind === "handle-revoked" ||
      sqliteState.kind === "needs-permission");
  const cachedOpfsName =
    sqliteState?.kind === "attached" || sqliteState?.kind === "needs-permission"
      ? (sqliteState.record.opfsName ?? null)
      : null;
  const fileNameFromHandle =
    sqliteState && sqliteState.kind !== "needs-reattach-this-device"
      ? (sqliteState.record.fileName ?? sqliteState.record.handle.name ?? null)
      : null;

  const handleDelete = async () => {
    setError(null);
    setPendingDelete(true);
    try {
      await remove(connection.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete");
    } finally {
      setPendingDelete(false);
    }
  };

  const handleClearCache = async () => {
    if (!cachedOpfsName) return;
    setError(null);
    setPendingClearCache(true);
    try {
      // Close any live connection first — `unlink` is undefined
      // behavior on a SAH file with an open DB.
      sendMessageToWorker({
        task: TaskType.SqliteCloseConnection,
        data: { savedConnectionId: connection.id },
      });
      sendMessageToWorker({
        task: TaskType.SqliteClearOpfsCache,
        data: { savedConnectionId: connection.id, opfsName: cachedOpfsName },
      });
      await setOpfsNameForHandle(connection.id, null);
      bumpState();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear cache");
    } finally {
      setPendingClearCache(false);
    }
  };

  const handleReconnect = async () => {
    setError(null);
    let picked;
    try {
      picked = await pickSqliteFile();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't open the file.");
      return;
    }
    if (picked.kind === "unsupported") {
      setError("This browser can't open files. Open in Chrome, Edge, or Arc.");
      return;
    }
    if (picked.kind === "cancelled") return;

    setPendingReconnect(true);
    let result: ReattachFlowResult;
    try {
      result = await reattachLocalSqlite(
        { savedConnection: connection, handle: picked.handle },
        {
          attachToOpfs: (_file, h) => attachSqliteToOpfsViaWorker(worker, h),
          unlinkOpfs: async (opfsName) => {
            // Fire-and-forget — next attach LRU-evicts the orphan if needed.
            sendMessageToWorker({
              task: TaskType.SqliteClearOpfsCache,
              data: { savedConnectionId: connection.id, opfsName },
            });
          },
        },
      );
    } catch (e) {
      setPendingReconnect(false);
      setError(e instanceof Error ? e.message : "Reconnect failed.");
      return;
    }
    setPendingReconnect(false);

    switch (result.kind) {
      case "reattached":
        await refetch().catch(() => undefined);
        bumpState();
        return;
      case "hash-mismatch":
        setError(
          "That file doesn't match this connection. The contents have changed — pick the original file, or delete this connection and create a new one for the updated file.",
        );
        return;
      case "blocked-too-large":
        setError(
          `Not enough browser storage. Needs ${formatBytes(result.fileSize)}, only ${formatBytes(result.remainingBytes)} free.`,
        );
        return;
      case "unsupported-browser":
        setError(result.reason);
        return;
      case "user-cancelled":
        return;
    }
  };

  return (
    <div className="flex flex-col gap-3.5">
      {error && <InlineError>{error}</InlineError>}
      {!capability.ok && (
        <Card variant="subtle">
          <p className="text-fg-bold">This browser can't open Local SQLite.</p>
          <p className="text-fg-muted-regular-small" style={{ marginTop: 6 }}>
            {capability.message}
          </p>
        </Card>
      )}

      {sqliteState && (
        <SqliteStatusPill
          state={sqliteState}
          lastUsedAt={connection.last_used_at}
        />
      )}

      {needsReconnect && capability.ok && (
        <ReconnectCard
          onReconnect={handleReconnect}
          pending={pendingReconnect}
        />
      )}

      <FileDetailsGrid
        label={connection.display_name}
        fileName={fileNameFromHandle}
        fileSize={cr.file_size}
        sha256={cr.sha256_hash}
        createdAt={connection.created_at}
        lastUsedAt={connection.last_used_at}
      />

      {cachedOpfsName && isAttached && (
        <div
          className="flex items-center justify-between gap-3 p-2 rounded-app border border-border"
          style={{ background: "var(--color-bg-hover)" }}
        >
          <p className="text-fg-muted-regular-small">
            <span className="text-fg" style={{ fontWeight: 500 }}>
              Cached locally
            </span>{" "}
            for faster re-opening. Reconnecting from your file will recopy it.
          </p>
          <button
            type="button"
            className="px-2 py-1 rounded-app text-fg-muted text-small font-medium hover:bg-bg disabled:opacity-50 shrink-0"
            onClick={handleClearCache}
            disabled={pendingClearCache}
          >
            {pendingClearCache ? "Clearing…" : "Clear cached copy"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-end">
        <button
          type="button"
          className="px-1.5 py-1.5 rounded-app text-error text-small font-medium hover:bg-error/10 disabled:opacity-50"
          onClick={handleDelete}
          disabled={pendingDelete}
        >
          {pendingDelete ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  );
};

/** Status pill — mirrors the Google overlay's `StatusPill`. Two
 *  user-facing states collapse the four underlying handle states: an
 *  attached connection (green) or one that needs reconnecting (amber).
 *  The reason for the reconnect (missing handle, denied permission,
 *  file moved) is intentionally not surfaced — the remedy is the same
 *  picker either way. */
const SqliteStatusPill: React.FC<{
  state: SqliteHandleState;
  lastUsedAt: number | null;
}> = ({ state, lastUsedAt }) => {
  const attached = state.kind === "attached";
  const dotClass = attached ? "bg-success" : "bg-warning";
  const label = attached
    ? "Attached on this device"
    : "Needs to reconnect on this device";
  const meta =
    attached && lastUsedAt
      ? `last opened ${formatRelativeTime(lastUsedAt * 1000)}`
      : null;
  return (
    <div className="inline-flex items-center gap-2 self-start px-2 py-1 rounded-app border border-border bg-bg-hover">
      <span
        aria-hidden="true"
        className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`}
      />
      <span className="text-fg text-small font-medium">{label}</span>
      {meta && (
        <>
          <span aria-hidden="true" className="text-border">
            ·
          </span>
          <span className="text-fg-muted-regular-small">{meta}</span>
        </>
      )}
    </div>
  );
};

/** Warning card prompting the user to pick the file again. Used for
 *  every non-attached state — same remedy, same UI. */
const ReconnectCard: React.FC<{
  onReconnect: () => void;
  pending: boolean;
}> = ({ onReconnect, pending }) => (
  <div
    className="rounded-app p-3"
    style={{
      background: "var(--color-warning-bg)",
      border: "1px solid var(--color-warning)",
    }}
  >
    <p className="text-fg-bold">Reconnect this file</p>
    <p
      className="text-fg-muted-regular-small"
      style={{ marginTop: 4, lineHeight: 1.5 }}
    >
      Mycorr can't open the file from this browser right now. Pick it again and
      we'll verify it's the same file by its hash.
    </p>
    <div style={{ marginTop: 8 }}>
      <Button
        variant="primary"
        size="sm"
        onClick={onReconnect}
        disabled={pending}
      >
        {pending ? "Reconnecting…" : "Reconnect file…"}
      </Button>
    </div>
  </div>
);

/** Two-column label/value grid. Filename and sha256 rows include
 *  inline copy buttons. */
const FileDetailsGrid: React.FC<{
  label: string;
  fileName: string | null;
  fileSize: number;
  sha256: string;
  createdAt: number;
  lastUsedAt: number | null;
}> = ({ label, fileName, fileSize, sha256, createdAt, lastUsedAt }) => {
  return (
    <div>
      <p className="text-fg-semibold mb-1.5">File details</p>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "100px 1fr",
          rowGap: 6,
          columnGap: 12,
          margin: 0,
          fontSize: "var(--font-size-small)",
        }}
      >
        <dt className="text-fg-muted-regular-small">Label</dt>
        <dd className="text-fg" style={{ margin: 0 }}>
          {label}
        </dd>

        {fileName && (
          <>
            <dt className="text-fg-muted-regular-small">File name</dt>
            <dd
              className="text-fg font-mono"
              style={{
                margin: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                wordBreak: "break-all",
              }}
            >
              {fileName}
              <CopyButton text={fileName} ariaLabel="Copy file name" />
            </dd>
          </>
        )}

        <dt className="text-fg-muted-regular-small">Size</dt>
        <dd
          className="text-fg"
          style={{ margin: 0, fontVariantNumeric: "tabular-nums" }}
        >
          {formatBytes(fileSize)}
        </dd>

        <dt className="text-fg-muted-regular-small">Added</dt>
        <dd className="text-fg" style={{ margin: 0 }}>
          {formatRelativeTime(createdAt * 1000)}
        </dd>

        {lastUsedAt && (
          <>
            <dt className="text-fg-muted-regular-small">Last opened</dt>
            <dd className="text-fg" style={{ margin: 0 }}>
              {formatRelativeTime(lastUsedAt * 1000)}
            </dd>
          </>
        )}

        <dt className="text-fg-muted-regular-small">sha256</dt>
        <dd
          className="text-fg font-mono"
          style={{
            margin: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            wordBreak: "break-all",
          }}
        >
          <span>{sha256}</span>
          <CopyButton text={sha256} ariaLabel="Copy sha256" />
        </dd>
      </dl>
    </div>
  );
};

const BucketConnectionView: React.FC<{
  connection: SavedConnection;
  onClose: () => void;
  onSaved: () => void;
}> = ({ connection, onClose, onSaved }) => {
  const cr =
    connection.credentials.type === "bucket" ? connection.credentials : null;
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState(false);
  const remove = useConnectionsStore((s) => s.remove);
  // The connections list already carries the bucket's non-secret auth summary
  // (method, audience, linked Google connection id), so the overlay needs no
  // extra fetch — it resolves the linked account email from the already-loaded
  // Google connection.
  const linkedId = cr?.linked_google_saved_connection_id ?? null;
  const linked = useConnectionsStore((s) =>
    linkedId ? findConnection(s, linkedId) : undefined,
  );

  if (!cr) return null;

  const linkedEmail =
    linked?.credentials.type === "google_oauth"
      ? linked.credentials.account_email
      : null;

  const handleDisconnect = async () => {
    setError(null);
    setPendingDelete(true);
    try {
      await remove(connection.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to disconnect");
    } finally {
      setPendingDelete(false);
    }
  };

  return (
    <div className="flex flex-col gap-3.5">
      {error && <InlineError>{error}</InlineError>}

      <BucketStatusPill authMethod={cr.auth_method ?? null} />

      <BucketDetailsGrid
        cr={cr}
        email={linkedEmail}
        audience={cr.wif_audience ?? null}
      />

      <p className="text-fg-muted-regular-small">
        Credentials are encrypted at rest and never returned to the browser. To
        rotate them, delete this connection and add a new one with the new
        credentials.
      </p>
      <div className="flex items-center justify-end">
        <button
          type="button"
          className="px-1.5 py-1.5 rounded-app text-error text-small font-medium hover:bg-error/10 disabled:opacity-50"
          onClick={handleDisconnect}
          disabled={pendingDelete}
        >
          {pendingDelete ? "Disconnecting…" : "Disconnect"}
        </button>
      </div>
    </div>
  );
};

const REAUTH_SUCCESS_DURATION_MS = 2000;

type BucketAuthMethod = "user_oauth_direct" | "user_oauth_federation";

const BUCKET_AUTH_LABEL: Record<BucketAuthMethod, string> = {
  user_oauth_direct: "Sign in with Google",
  user_oauth_federation: "Workload Identity",
};

const BucketStatusPill: React.FC<{
  authMethod: BucketAuthMethod | null;
}> = ({ authMethod }) => {
  const methodLabel = authMethod ? BUCKET_AUTH_LABEL[authMethod] : null;
  return (
    <div className="inline-flex items-center gap-2 self-start px-2 py-1 rounded-app border border-border bg-bg-hover">
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full bg-success"
      />
      <span className="text-fg text-small font-medium">Connected</span>
      {methodLabel && (
        <>
          <span aria-hidden="true" className="text-border">
            ·
          </span>
          <span className="text-fg-muted-regular-small">{methodLabel}</span>
        </>
      )}
    </div>
  );
};

const BucketDetailsGrid: React.FC<{
  cr: {
    provider: keyof typeof BUCKET_PROVIDER_LABEL;
    scope: { kind: "bucket"; name: string } | { kind: "account" };
  };
  email: string | null;
  audience: string | null;
}> = ({ cr, email, audience }) => {
  const scopeLine =
    cr.scope.kind === "bucket"
      ? cr.scope.name
      : "Entire account (every bucket in the project)";
  const providerLabel = BUCKET_PROVIDER_LABEL[cr.provider] ?? cr.provider;
  return (
    <div>
      <p className="text-fg-semibold mb-1.5">Details</p>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "140px 1fr",
          rowGap: 6,
          columnGap: 12,
          margin: 0,
          fontSize: "var(--font-size-small)",
        }}
      >
        <dt className="text-fg-muted-regular-small">Provider</dt>
        <dd className="text-fg" style={{ margin: 0 }}>
          {providerLabel}
        </dd>

        <dt className="text-fg-muted-regular-small">Scope</dt>
        <dd
          className="text-fg font-mono"
          style={{ margin: 0, wordBreak: "break-all" }}
        >
          {scopeLine}
        </dd>

        <dt className="text-fg-muted-regular-small">Google account</dt>
        <dd className="text-fg" style={{ margin: 0, wordBreak: "break-all" }}>
          {email ?? <span className="text-fg-muted-regular">—</span>}
        </dd>

        {audience && (
          <>
            <dt className="text-fg-muted-regular-small">WIF audience</dt>
            <dd
              className="text-fg font-mono"
              style={{
                margin: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                wordBreak: "break-all",
              }}
            >
              <span>{audience}</span>
              <CopyButton text={audience} ariaLabel="Copy WIF audience" />
            </dd>
          </>
        )}
      </dl>
    </div>
  );
};

const GoogleConnectionView: React.FC<{
  connection: SavedConnection;
  onClose: () => void;
  onSaved: () => void;
}> = ({ connection }) => {
  const cr =
    connection.credentials.type === "google_oauth"
      ? connection.credentials
      : null;
  const [error, setError] = useState<string | null>(null);
  const [justReauthorized, setJustReauthorized] = useState(false);
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { start: startReauth, pending: reauthPending } = useGoogleOauthPopup();
  const refetch = useConnectionsStore((s) => s.refetch);

  useEffect(
    () => () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    },
    [],
  );

  if (!cr) return null;

  const handleReauth = async () => {
    setError(null);
    const outcome = await startReauth({
      savedConnectionId: connection.id,
      clientType: "sheets",
    });
    if (outcome.status === "ok") {
      await refetch();
      setJustReauthorized(true);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(
        () => setJustReauthorized(false),
        REAUTH_SUCCESS_DURATION_MS,
      );
    } else if (outcome.status === "email_mismatch") {
      setError(
        `Reauthorize requires the same Google account (${outcome.expected}). To switch accounts, delete this connection and add a new one.`,
      );
    } else if (outcome.status === "popup_blocked") {
      setError("Allow popups for this site, then click Reauthorize again.");
    } else if (outcome.status === "cancelled") {
      setError("Cancelled — try again when you're ready.");
    } else if (outcome.status === "error") {
      setError(outcome.message);
    }
  };

  const expiry = expiryStatus(cr.access_token_expires_at);
  const hasSheets = hasGoogleSheetsScope(cr);

  return (
    <div className="flex flex-col gap-3.5">
      {error && <InlineError>{error}</InlineError>}

      <div className="flex items-center gap-1.5 self-start">
        <StatusPill
          expiry={expiry}
          hasSheetsScope={hasSheets}
          justReauthorized={justReauthorized}
        />
        <TooltipButton
          title={reauthPending ? "Reauthorizing…" : "Reauthorize"}
          variant="default"
          size="sm"
          onClick={handleReauth}
          disabled={reauthPending}
          aria-label="Reauthorize"
        >
          <RefreshIcon sx={{ fontSize: 14 }} />
        </TooltipButton>
      </div>

      {cr.scopes.length > 0 && (
        <div>
          <p className="text-fg-semibold mb-1.5">Permissions</p>
          <div className="flex flex-wrap gap-1">
            {cr.scopes.map((url) => (
              <ScopeChip key={url} url={url} />
            ))}
          </div>
        </div>
      )}

      <p className="text-fg-muted-regular-small">
        The refresh token is encrypted on the server and never leaves it.
        Reauthorize if Google asks you to grant access again.
      </p>
    </div>
  );
};

type ScopeTier = "read" | "write" | "admin";
interface ScopeMeta {
  label: string;
  tag: string;
  tier: ScopeTier;
  description: string;
}

const GOOGLE_SCOPES: Record<string, ScopeMeta> = {
  "https://www.googleapis.com/auth/spreadsheets": {
    label: "Sheets",
    tag: "read & write",
    tier: "write",
    description: "See and edit any of your Google Sheets",
  },
  "https://www.googleapis.com/auth/spreadsheets.readonly": {
    label: "Sheets",
    tag: "read",
    tier: "read",
    description: "See your Google Sheets but not modify them",
  },
  "https://www.googleapis.com/auth/drive.file": {
    label: "Drive",
    tag: "files you pick",
    tier: "write",
    description:
      "Only files you pick with Google Picker — never your whole Drive",
  },
  "https://www.googleapis.com/auth/drive.readonly": {
    label: "Drive",
    tag: "read",
    tier: "read",
    description: "See files in your Drive but not modify them",
  },
  "https://www.googleapis.com/auth/drive": {
    label: "Drive",
    tag: "full access",
    tier: "admin",
    description: "See, create, and delete any file in your Drive",
  },
  "https://www.googleapis.com/auth/userinfo.email": {
    label: "Profile",
    tag: "email",
    tier: "read",
    description: "See your name and email address",
  },
  "https://www.googleapis.com/auth/userinfo.profile": {
    label: "Profile",
    tag: "name & photo",
    tier: "read",
    description: "See your name and profile photo",
  },
  openid: {
    label: "OpenID",
    tag: "sign-in",
    tier: "read",
    description: "Confirm your Google identity",
  },
};

function scopeMeta(url: string): ScopeMeta {
  const known = GOOGLE_SCOPES[url];
  if (known) return known;
  const tail = url.split("/").pop() || url;
  return { label: tail, tag: "", tier: "read", description: url };
}

const TIER_DOT_CLASS: Record<ScopeTier, string> = {
  read: "bg-fg-muted",
  write: "bg-warning",
  admin: "bg-error",
};

const TIER_TAG_BG_CLASS: Record<ScopeTier, string> = {
  read: "bg-bg-hover",
  write: "bg-warning/10",
  admin: "bg-error/10",
};

const ScopeChip: React.FC<{ url: string }> = ({ url }) => {
  const meta = scopeMeta(url);
  return (
    <Tooltip title={meta.description}>
      <span
        data-scope={url}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-app border border-border bg-bg-hover font-mono text-small text-fg cursor-default"
      >
        {meta.label}
        {meta.tag && (
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-app text-fg-muted-medium-xs font-sans ${TIER_TAG_BG_CLASS[meta.tier]}`}
          >
            <span
              aria-hidden="true"
              className={`inline-block w-1 h-1 rounded-full ${TIER_DOT_CLASS[meta.tier]}`}
            />
            {meta.tag}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

type ExpiryStatus =
  | { kind: "fresh" }
  | { kind: "soon"; minutes: number }
  | { kind: "expired" }
  | { kind: "unknown" };

function expiryStatus(unixSeconds: number | undefined): ExpiryStatus {
  if (!unixSeconds || unixSeconds <= 0) return { kind: "unknown" };
  const deltaSec = unixSeconds - Date.now() / 1000;
  if (deltaSec < 0) return { kind: "expired" };
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin <= 5) return { kind: "soon", minutes: deltaMin };
  return { kind: "fresh" };
}

export const StatusPill: React.FC<{
  expiry: ExpiryStatus;
  hasSheetsScope: boolean;
  justReauthorized?: boolean;
}> = ({ expiry, hasSheetsScope, justReauthorized = false }) => {
  // Sheets-scope absence takes precedence over expiry warnings — the
  // credential is unusable for the Picker regardless of how fresh the
  // token is. Reauthorize is the only remedy either way.
  const { dotClass, meta } = justReauthorized
    ? { dotClass: null, meta: "Reauthorized" }
    : !hasSheetsScope
      ? {
          dotClass: "bg-warning",
          meta: "Sheets not authorized",
        }
      : expiry.kind === "soon"
        ? {
            dotClass: "bg-warning",
            meta: `token expires in ${expiry.minutes}m`,
          }
        : expiry.kind === "expired"
          ? {
              dotClass: "bg-warning",
              meta: "session will renew on next sync",
            }
          : { dotClass: "bg-success", meta: null };

  return (
    <div className="inline-flex items-center gap-2 self-start px-2 py-1 rounded-app border border-border bg-bg-hover">
      {dotClass !== null ? (
        <span
          aria-hidden="true"
          className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`}
        />
      ) : (
        <CheckIcon
          aria-hidden="true"
          className="status-pill-check text-success"
          sx={{ fontSize: 12 }}
        />
      )}
      <span className="text-fg text-small font-medium">Connected</span>
      {meta && (
        <>
          <span aria-hidden="true" className="text-border">
            ·
          </span>
          <span className="text-fg-muted-regular-small">{meta}</span>
        </>
      )}
    </div>
  );
};
